import axios from "axios";

// Prefer same-origin in the browser (eliminates CORS, allows HttpOnly cookies).
// Fall back to REACT_APP_BACKEND_URL when window is not available (SSR/tests).
const RUNTIME_ORIGIN = typeof window !== "undefined" ? window.location.origin : null;
const BUILD_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = RUNTIME_ORIGIN || BUILD_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth
export const exchangeSession = (sessionId) =>
  api.post("/auth/session", { session_id: sessionId });
export const getMe = () => api.get("/auth/me");
export const logout = () => api.post("/auth/logout");
export const setRole = (role) => api.post("/auth/set-role", { role });
export const registerUser = (email, password, name) =>
  api.post("/auth/register", { email, password, name });
export const loginUser = (email, password) =>
  api.post("/auth/login", { email, password });

// Users
export const getUserProfile = (userId) => api.get(`/users/${userId}`);
export const updateProfile = (data) => api.put("/users/profile", data);

// Listings
export const getListings = (params) => api.get("/listings", { params });
export const getListing = (id) => api.get(`/listings/${id}`);
export const createListing = (data) => api.post("/listings", data);
export const updateListing = (id, data) => api.put(`/listings/${id}`, data);
export const deleteListing = (id) => api.delete(`/listings/${id}`);
export const getMyListings = () => api.get("/my-listings");

// File uploads
export const uploadFile = (file, category = "general") => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", category);
  return api.post("/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const uploadVideoIntro = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/upload/video-intro", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const uploadListingPhoto = (listingId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/listings/${listingId}/photos`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const uploadListingVideo = (listingId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/listings/${listingId}/video`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getFileUrl = (path) => `${API}/files/${path}`;
export const photoUrl = (p) => (!p || /^https?:\/\//.test(p) ? p : getFileUrl(p));

// Payments
export const createCheckout = (paymentType, originUrl, listingId) =>
  api.post("/payments/checkout", {
    payment_type: paymentType,
    origin_url: originUrl,
    listing_id: listingId,
  });
export const getPaymentStatus = (sessionId) =>
  api.get(`/payments/status/${sessionId}`);

// Messages
export const getConversations = () => api.get("/conversations");
export const getMessages = (convId) =>
  api.get(`/conversations/${convId}/messages`);
export const sendMessage = (convId, text) =>
  api.post(`/conversations/${convId}/messages`, { text });
export const createConversation = (recipientId, initialMessage, listingId) =>
  api.post("/conversations", {
    recipient_id: recipientId,
    listing_id: listingId,
    initial_message: initialMessage,
  });

// Showings
export const createShowing = (data) => api.post("/showings", data);
export const getShowings = () => api.get("/showings");
export const updateShowing = (id, status) =>
  api.put(`/showings/${id}`, { status });

// Ratings
export const createRating = (data) => api.post("/ratings", data);
export const getUserRatings = (userId) => api.get(`/ratings/user/${userId}`);

// AI
export const getAiMatches = () => api.get("/ai/matches");

// Leases
export const createLease = (data) => api.post("/leases", data);
export const signLease = (leaseId) => api.post(`/leases/${leaseId}/sign`);
export const getLeases = () => api.get("/leases");

// Admin
export const getAdminStats = () => api.get("/admin/stats");
export const getAdminUsers = () => api.get("/admin/users");
export const getAdminListings = (status) =>
  api.get("/admin/listings", { params: status ? { status } : {} });
export const verifyListing = (id) => api.put(`/admin/listings/${id}/verify`);
export const rejectListing = (id, reason) =>
  api.put(`/admin/listings/${id}/reject`, { reason });
export const adminSetRole = (userId, role) =>
  api.put(`/admin/users/${userId}/role`, { role });
export const getAdminTransactions = () => api.get("/admin/transactions");

// Seed
export const seedData = () => api.post("/seed");

// Forgot/Reset Password
export const forgotPassword = (email) =>
  api.post("/auth/forgot-password", { email });
export const resetPassword = (token, newPassword) =>
  api.post("/auth/reset-password", { token, new_password: newPassword });

// Email Verification
export const verifyEmail = (token) => api.get(`/auth/verify-email?token=${token}`);
export const resendVerificationEmail = () => api.post("/auth/resend-verification");

// Stripe Connect
export const stripeConnectOnboard = (originUrl) =>
  api.post("/stripe/connect/onboard", { origin_url: originUrl });
export const getStripeConnectStatus = () => api.get("/stripe/connect/status");
export const stripeConnectDisconnect = () => api.post("/stripe/connect/disconnect");

// Contact & Waitlist
export const submitContact = (data) => api.post("/contact", data);
export const joinWaitlist = (data) => api.post("/waitlist", data);
